/**
 * GoalCard Component
 *
 * Displays a single operator goal with progress bar, target level,
 * trend indicator, and completion badge.
 *
 * Uses OperatorCard as wrapper and score-colors for progress bar coloring.
 * No hover effects — uses active: press states via OperatorCard.
 */

import { Target, TrendingUp, TrendingDown, Minus, CheckCircle2 } from 'lucide-react';
import type { OperatorGoalResponse } from '@guild/shared';
import { OperatorCard } from './OperatorCard';
import { getScoreColor } from '../../../lib/score-colors';
import { getRubricLabel } from '../utils';
import { cn } from '../../../lib/utils';

/** Literal bg classes for progress bar fill — must be literal strings for Tailwind purge. */
function getProgressBarBg(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'bg-gray-200';
  if (score >= 80) return 'bg-emerald-600';
  if (score >= 60) return 'bg-amber-600';
  return 'bg-red-600';
}

interface GoalCardProps {
  goal: OperatorGoalResponse;
  /** Staggered fadeInUp animation index */
  index?: number;
  /** Tap handler to open goal detail sheet */
  onPress?: (goal: OperatorGoalResponse) => void;
}

export function GoalCard({ goal, index = 0, onPress }: GoalCardProps) {
  const isCompleted = goal.status === 'completed';
  const hasScore = goal.currentScore !== null && goal.currentScore !== undefined;
  const currentScore = goal.currentScore ?? 0;
  const fillPercent = Math.min((currentScore / goal.targetScore) * 100, 100);
  const progressColorClass = getScoreColor(goal.currentScore, 'text');
  const progressBarBg = getProgressBarBg(goal.currentScore);

  return (
    <div
      style={{
        animation: `fadeInUp 0.4s ease-out ${index < 5 ? index * 0.06 : 0}s both`,
      }}
    >
      <OperatorCard onClick={onPress ? () => onPress(goal) : undefined}>
        <div className="flex items-start gap-3">
          {/* Target icon */}
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-guild-green/10 text-guild-green">
            <Target size={22} strokeWidth={1.5} />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Title row + badge */}
            <div className="mb-1 flex items-start justify-between gap-2">
              <h3 className="text-base font-semibold text-op-text-primary line-clamp-1">
                {goal.sopTitle}
              </h3>
              {isCompleted && (
                <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                  <CheckCircle2 size={11} strokeWidth={1.5} />
                  Completed
                </span>
              )}
            </div>

            {/* Target label */}
            <p className="mb-2 text-sm text-op-text-secondary">
              Target: {getRubricLabel(goal.targetScore)}
            </p>

            {/* Progress bar */}
            <div className="mb-1.5">
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      isCompleted ? 'bg-emerald-500' : progressBarBg,
                    )}
                    style={{ width: `${isCompleted ? 100 : fillPercent}%` }}
                  />
                </div>
                {/* Score labels */}
                <span className="flex-shrink-0 text-xs font-medium text-op-text-secondary">
                  {hasScore ? (
                    <>
                      <span className={progressColorClass}>{getRubricLabel(currentScore)}</span>
                      <span className="mx-0.5 text-op-text-tertiary">&rarr;</span>
                      <span>{getRubricLabel(goal.targetScore)}</span>
                    </>
                  ) : (
                    <span className="text-op-text-tertiary">Not yet assessed</span>
                  )}
                </span>
              </div>
            </div>

            {/* Trend indicator */}
            {goal.trend !== null && goal.trend !== undefined && (
              <TrendIndicator trend={goal.trend} />
            )}
          </div>
        </div>
      </OperatorCard>
    </div>
  );
}

// ============================================
// TREND INDICATOR
// ============================================

function TrendIndicator({ trend }: { trend: number }) {
  if (trend >= 5) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
        <TrendingUp size={14} strokeWidth={1.5} />
        Improving this month
      </span>
    );
  }

  if (trend <= -5) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
        <TrendingDown size={14} strokeWidth={1.5} />
        Declining this month
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-op-text-tertiary">
      <Minus size={14} strokeWidth={1.5} />
      Steady
    </span>
  );
}
